import {
  AgendaSlotType,
  FULLVISIO_SLOT_TYPE,
  isFullvisioSlotType,
  PRESENTIEL_SLOT_TYPE,
} from '@omecare/utils';
import { VideoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { capitalizeFirst } from '@/lib/capitalize-first';
import { formatRetrocessionRate } from '../../slot-sheet/format-retrocession-rate';
import { SlotSheetCheckboxRow } from '../../slot-sheet/slot-sheet-checkbox-row';
import {
  SlotSheetDefinitionList,
  SlotSheetRow,
  SlotSheetSection,
} from '../../slot-sheet/slot-sheet-section';
import { ViewAdminAgendaSlotSheetViewModel } from '../view-admin-agenda-slot-sheet.viewmodel';

/**
 * "Créneau" section of the admin slot sheet: period, consultation mode,
 * assistant mode and retrocession rate.
 *
 * @param props.viewModel - the sheet view model
 * @param props.timeLabel - the slot's time range, when it has one
 * @param props.canEdit - whether the viewer may change the slot's settings
 * @returns the section element
 */
export const ViewAdminAgendaSlotSectionSlot = ({
  viewModel,
  timeLabel,
  canEdit,
}: {
  viewModel: ViewAdminAgendaSlotSheetViewModel;
  timeLabel: string | null;
  canEdit: boolean;
}) => {
  const { t } = useTranslation();

  // Remote (fullvisio) slots are always "sans assistant·e", so the SA override
  // is forced on and not editable while the slot is fullvisio.
  const isFullvisio = isFullvisioSlotType(viewModel.information.slotType);
  const rate = formatRetrocessionRate(viewModel.information.retrocessionRate);

  return (
    <SlotSheetSection title={t('slot-sheet.section.slot')}>
      <SlotSheetDefinitionList>
        <SlotSheetRow label={t('slot-sheet.field.period')}>
          {capitalizeFirst(
            t(`period-of-day.${viewModel.information.periodOfTheDay}`),
          )}
          {timeLabel && (
            <span className="text-muted-foreground"> · {timeLabel}</span>
          )}
        </SlotSheetRow>

        <SlotSheetRow
          label={t('view-admin-agenda-slot-sheet.information.slot-type')}
        >
          <div className="flex items-center gap-2">
            {isFullvisio && (
              <VideoIcon
                className="size-3.5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            )}
            <Select
              value={viewModel.information.slotType}
              disabled={!canEdit || viewModel.updateAssistantModeLoading}
              onValueChange={(value) =>
                viewModel.api.updateConsultationMode(value as AgendaSlotType)
              }
            >
              <SelectTrigger
                size="sm"
                className="w-44"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PRESENTIEL_SLOT_TYPE}>
                  {t('agenda-slot.types.mixed')}
                </SelectItem>
                <SelectItem value={FULLVISIO_SLOT_TYPE}>
                  {t('agenda-slot.types.fullvisio')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </SlotSheetRow>

        {/* Assistant mode (AA/SA) — read-only; derived from the assistant
            planning when the slot was opened, frozen on the slot once booked.
            Flagged "(auto)" when the mode is not forced on the slot and is
            inferred from the assistant planning. */}
        <SlotSheetRow
          label={t('view-admin-agenda-slot-sheet.information.assistant-mode')}
        >
          <span className="flex items-center gap-1.5">
            {viewModel.information.withAssistant
              ? t('assistant-mode.with-assistant')
              : t('assistant-mode.without-assistant')}
            {viewModel.information.withAssistantInferred && (
              <Badge
                variant="secondary"
                className="px-1.5 py-0 font-normal text-[10px]"
              >
                {t(
                  'view-admin-agenda-slot-sheet.information.assistant-mode-auto',
                )}
              </Badge>
            )}
          </span>
        </SlotSheetRow>

        {/* Force without assistant (SA) — editable override */}
        <SlotSheetCheckboxRow
          label={t(
            'view-admin-agenda-slot-sheet.force-without-assistant.label',
          )}
          description={t(
            'view-admin-agenda-slot-sheet.force-without-assistant.description',
          )}
          checked={viewModel.information.forceWithoutAssistant || isFullvisio}
          disabled={
            !canEdit || viewModel.updateAssistantModeLoading || isFullvisio
          }
          onCheckedChange={viewModel.api.updateForceWithoutAssistant}
        />

        {rate && (
          <SlotSheetRow
            label={t(
              'view-admin-agenda-slot-sheet.information.retrocession-rate',
            )}
          >
            <span className="tabular-nums">
              {rate.percent}
              <span className="ml-1 text-muted-foreground">({rate.ratio})</span>
            </span>
          </SlotSheetRow>
        )}
      </SlotSheetDefinitionList>
    </SlotSheetSection>
  );
};
